import React, { useState } from 'react';
import { TimelineEventSummary, CitationItem } from './components/Interface';
import {
  PictureAsPdf as PDFIcon,
  Notes as NotesIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
  SensorOccupied as ImagingIcon,
  Science as PathologyIcon,
  Bloodtype as LabIcon,
  AssignmentInd as PatientReportedIcon,
  Mail as LetterIcon,
  Medication as PrescriptionIcon,
  LocalHospital as TreatmentIcon,
  MedicalServices as DiagnosisIcon,
  CalendarToday as AppointmentIcon,
} from '@mui/icons-material';

export interface ContentItem {
  text: string;
  subItems?: { text: string }[];
}

export interface UseCopyToClipboardReturn {
  isSelectedCopy: boolean;
  copySnackbarOpen: boolean;
  copyError: boolean;
  copyToClipboard: (text: string) => Promise<void>;
  setCopySnackbarOpen: (open: boolean) => void;
}

/**
 * Deduplicates citations for Notes with the same resource ID (documentUrl)
 * Keeps the first occurrence of each unique Note resource
 */
export const deduplicateNoteCitations = (citations: CitationItem[]): CitationItem[] => {
  const seenNoteResourceIds = new Set<string>();

  return citations.filter((citation) => {
    // If it's not a Note, always include it
    if (citation.documentType !== 'Note') {
      return true;
    }

    // For Notes, check if we've seen this resource ID before
    const resourceId = citation.documentUrl;
    if (!resourceId) {
      return true; // Include Notes without resource ID (shouldn't happen but be safe)
    }

    if (seenNoteResourceIds.has(resourceId)) {
      return false; // Skip duplicate Note
    }

    seenNoteResourceIds.add(resourceId);
    return true; // Include first occurrence of this Note
  });
};

/**
 * Extracts clean text content from timeline or summary content, removing HTML tags
 * and formatting with bullet points for hierarchical structure
 */
export const extractTextContent = (contents: ContentItem[]): string => {
  return contents
    .map((content) => {
      // Remove HTML tags and get clean text
      const cleanText = content.text.replace(/<[^>]*>/g, '');
      const subItemsText = content.subItems?.map((subItem) => subItem.text.replace(/<[^>]*>/g, '')).join('\n• ') || '';

      return subItemsText ? `${cleanText}\n• ${subItemsText}` : cleanText;
    })
    .join('\n\n');
};

export const formatTimelineContent = (title: string, date: string, contents: TimelineEventSummary[]): string => {
  return `${title}\n${date}\n\n${extractTextContent(contents)}`;
};

export const removeBulletPoints = (text: string): string => {
  if (!text) return text;

  let cleanText = text;

  // Remove HTML list elements while preserving their text content
  cleanText = cleanText.replace(/<\/?ul[^>]*>/gi, '');
  cleanText = cleanText.replace(/<\/?ol[^>]*>/gi, '');
  cleanText = cleanText.replace(/<li[^>]*>/gi, '');
  cleanText = cleanText.replace(/<\/li>/gi, '');

  // Remove various bullet point characters at the beginning of lines
  // This handles: •, *, -, +, >, ○, ●, ◦, etc.
  cleanText = cleanText.replace(/^[\s]*[•\*\-\+\>\○\●\◦]\s*/gm, '');

  // Remove numbered list items (e.g., "1. ", "2) ", "a. ", etc.)
  cleanText = cleanText.replace(/^[\s]*\d+[\.\)]\s*/gm, '');
  cleanText = cleanText.replace(/^[\s]*[a-zA-Z][\.\)]\s*/gm, '');

  // Clean up excessive whitespace that might be left behind
  cleanText = cleanText.replace(/^\s+/gm, ''); // Remove leading whitespace from lines

  return cleanText.trim();
};

export const formatSummaryContent = (title: string, contents: ContentItem[]): string => {
  return `${title}\n\n${extractTextContent(contents)}`;
};

/**
 * Memoized icon map to avoid creating new React elements on every call.
 * Maps backend resource types and document types to appropriate icons.
 */
const iconMap = {
  // File types
  pdf: <PDFIcon />,
  text: <InsertDriveFileIcon />,
  image: <ImageIcon />,

  // Resource types from backend
  documentpart: <PDFIcon />,
  note: <NotesIcon />,
  appointment: <AppointmentIcon />,
  labresultspanel: <LabIcon />,
  diagnosis: <DiagnosisIcon />,
  roprescription: <PrescriptionIcon />,
  rotreatment: <TreatmentIcon />,

  // Document type categories (from timeline)
  letter: <LetterIcon />,

  // Radiology/Imaging types
  radiology_ct: <ImagingIcon />,
  radiology_pet_ct: <ImagingIcon />,
  radiology_mri: <ImagingIcon />,
  radiology_x_ray: <ImagingIcon />,
  radiology_ultrasound: <ImagingIcon />,
  radiology_bone_scan: <ImagingIcon />,
  radiology_bone_density_scan: <ImagingIcon />,
  radiology_procedure: <ImagingIcon />,
  radiology_other: <ImagingIcon />,

  // Pathology types
  pathology_histopathology: <PathologyIcon />,
  pathology_cytopathology: <PathologyIcon />,
  pathology_other: <PathologyIcon />,
  pathology_blood_test: <LabIcon />,

  // Patient reported types
  distress_thermometer: <PatientReportedIcon />,
  health_assessment: <PatientReportedIcon />,

  default: <InsertDriveFileIcon />,
} as const;

/**
 * Returns an icon component based on the document type or resource type.
 * @param documentType - The type of document (e.g., 'pdf', 'note', 'DocumentPart', 'LabResultsPanel', 'RADIOLOGY_CT')
 * @returns A React icon component
 */
export const getDocumentTypeIcon = (documentType?: string | null): React.ReactNode => {
  try {
    // Handle null, undefined, or non-string values
    if (!documentType || typeof documentType !== 'string') {
      return iconMap.default;
    }

    const key = documentType.toLowerCase() as keyof typeof iconMap;
    return iconMap[key] ?? iconMap.default;
  } catch (error) {
    return iconMap.default;
  }
};

export const useCopyToClipboard = (): UseCopyToClipboardReturn => {
  const [isSelectedCopy, setIsSelectedCopy] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setIsSelectedCopy(true);
      setCopyError(false);
      setCopySnackbarOpen(true);

      // Reset button state after a short delay
      setTimeout(() => {
        setIsSelectedCopy(false);
      }, 1000);
    } catch (error) {
      setCopyError(true);
      setCopySnackbarOpen(true);
    }
  };

  return {
    isSelectedCopy,
    copySnackbarOpen,
    copyError,
    copyToClipboard,
    setCopySnackbarOpen,
  };
};
